import { AccordionDisclosure, AccountLogin, AnimatedDetails, AnnouncementBar, BuyButtons, CartCount, CartDrawer, CartNote, CartNotificationDrawer, CollectionList, ConfirmButton, CopyButton, CountrySelector, CustomButton, CustomCursor, DialogElement, Drawer, DropdownDisclosure, EffectCarousel, FacetApplyButton, FacetDialog, FacetDrawer, FacetFloatingFilter, FacetForm, FacetLink, FacetSortBy, FeatureChart, FreeShippingBar, GestureArea, GiftCardRecipient, HeightObserver, ImageBanner, ImageLinkBlocks, ImagesWithTextScrolling, ImpactText, LineItem, LineItemQuantity, Listbox, MarqueeText, MediaCarousel, MediaGrid, MediaWithText, MegaMenuDisclosure, MegaMenuPromoCarousel, ModelMedia, MultipleImagesWithText, MultipleImagesWithTextContentList, MultipleImagesWithTextImageList, NavigationDrawer, NewsletterPopup, NextButton, PageDots, PillLoader, Player, Popover, PredictiveSearch, PressCarousel, PrevButton, PriceRange, PrivacyBar, ProductCard, ProductForm, ProductGallery, ProductLoader, ProductQuickAdd, ProductRecommendations, ProductRerender, ProductZoomButton, ProgressBar, QuantitySelector, QuickBuyDrawer, RecentlyViewedProducts, ResizableTextarea, RevealItems, RevealedImage, SafeSticky, ScrollArea, ScrollCarousel, ScrollProgress, ScrollShadow, ScrollingText, SearchDrawer, SectionHeader, ShareButton, ShippingEstimator, ShopTheLookDots, Slideshow, SlideshowCarousel, SplitCursor, SplitLines, StoreHeader, Tabs, VariantPicker, VideoMedia, cachedFetch, createMediaImg, debounce, deepQuerySelector, extractSectionId, fetchCart, generateSrcset, getHeadingKeyframe, imageLoaded, throttle, waitForEvent } from "./theme.js";

// Updated Web Components
const MediaWithTextUpdated = class extends MediaWithText {
  constructor() {
    super()
  }

  connectedCallback() {
    this.initImageSizeControl();
    super.connectedCallback();
  }

  initImageSizeControl() {
    let imageEls = this.querySelectorAll(".media-with-text__media");
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.syncImageSize(imageEls)
      })
    });
    resizeObserver.observe(this);
  }

  syncImageSize(imageEls) {
    let largestImageSize = 0;
    this.style.setProperty('--largest-image-height', `0px`);
    imageEls.forEach(image => {
      if (image.clientHeight > largestImageSize) largestImageSize = image.clientHeight
    });
    this.style.setProperty('--largest-image-height', `${largestImageSize}px`);
  }

}

if (!customElements.get('media-with-text--updated')) {
  customElements.define('media-with-text--updated', MediaWithTextUpdated);
}


// Interactive Feature Tabs
const InteractiveFeatureTabs = class extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll(".interactive-feature-tabs__tab-title");
    this.contents = this.querySelectorAll(".interactive-feature-tabs__tab-content");
    this.images = this.querySelectorAll(".interactive-feature-tabs__image-container");

    this.sectionData = this.querySelector('script[data-section-data]') ? JSON.parse(this.querySelector('script[data-section-data]').textContent) : {};

    this.onTabClick = this.tabClick.bind(this);
  }

  connectedCallback() {
    const $this = this;

    this.tabs.forEach(tab => {
      tab.addEventListener("click", this.onTabClick);
    });

    if (document.readyState == "complete") {
      $this.initTextCarousel();
      $this.initImageCarousel();
    } else {
      document.addEventListener("DOMContentLoaded", function() {
        $this.initTextCarousel();
        $this.initImageCarousel();
      });
    }

    window.matchMedia("(min-width: 1000px)").addEventListener("change", $this.initImageCarousel.bind($this));

    if (Shopify.designMode) {
      this.closest(".shopify-section")?.addEventListener("shopify:block:select", this.onBlockSelect.bind(this));
    }
  }

  disconnectedCallback() {
    this.tabs.forEach(tab => {
      tab.removeEventListener("click", this.onTabClick);
    });
    window.matchMedia("(min-width: 1000px)").removeEventListener("change", this.initImageCarousel);
    this.closest(".shopify-section")?.removeEventListener("shopify:block:select", this.onBlockSelect);
  }

  onBlockSelect(data) {
    let blockID = data.detail.blockId;
    this.setActiveBlock(blockID);
  }

  tabClick(event) {
    const targetTab = event.target.closest(".interactive-feature-tabs__tab-title");
    if (targetTab.classList.contains("active")) return;
    this.setActiveBlock(targetTab.dataset.blockId);
  }

  setActiveBlock(blockID, source = null) {
    this.parentNode.style.setProperty('--background', this.sectionData[blockID].color);

    // Tabs
    this.tabs.forEach(tab => {
      tab.classList.toggle('active', tab.dataset.blockId == blockID);
    });

    // Content Carousel
    if (this.textCarousel && this.textCarousel.slides?.length > 0 && !this.textCarousel.slides.find(slide => slide.dataset.blockId == blockID).classList.contains("swiper-slide-active")) {
      this.textCarousel?.slideTo(this.textCarousel.slides.findIndex(slide => slide.dataset.blockId == blockID));
    }

    // Image Carousel
    if (this.imageCarousel && this.imageCarousel.slides?.length > 0 && source != 'carousel' && !this.imageCarousel.slides.find(slide => slide.dataset.blockId == blockID).classList.contains("swiper-slide-active")) {
      this.imageCarousel?.slideTo(this.imageCarousel.slides.findIndex(slide => slide.dataset.blockId == blockID));
    }
  }

  initTextCarousel() {
    let $this = this;
    this.textCarousel = new Swiper($this.querySelector(".interactive-feature-tabs__tabs-content-container"), {
      slidesPerView: 1,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      allowTouchMove: false
    });
  }

  initImageCarousel() {
    const $this = this;

    let options;
    if (window.matchMedia("(min-width: 1000px)").matches) {
      options = {
        slidesPerView: 1,
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        allowTouchMove: false
      }
    } else {
      options = {
        slidesPerView: "auto",
        spaceBetween: 24,
        centeredSlides: true,
        loop: true,
        speed: 400,
        on: {
          init: function() {
            this.loopFix();
          },
          slideChange: function() {
            let activeSlide = this.slides[this.activeIndex];
            let blockID = activeSlide.dataset.blockId;
            $this.setActiveBlock(blockID, 'carousel');
          }
        }
      }
    }
    
    this.imageCarousel?.destroy(true, true);
    this.imageCarousel = new Swiper($this.querySelector(".interactive-feature-tabs__images-container .swiper"), options);
  }
}

if (!customElements.get('interactive-feature-tabs')) {
  customElements.define('interactive-feature-tabs', InteractiveFeatureTabs);
}


// !Logo list
const RevealItemsCustom = class extends RevealItems {
  constructor() {
    super();
    this.carouselElement = this.querySelector(".swiper");
  }

  connectedCallback() {
    const $this = this;

    if (document.readyState == "complete") {
      this.initCarousel();
      window.addEventListener("resize", this.initCarousel.bind(this));
    } else {
      document.addEventListener("DOMContentLoaded", function() {
        $this.initCarousel();
        window.addEventListener("resize", $this.initCarousel.bind($this));
      });
    };

  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.initCarousel.bind(this))
  }

  initCarousel() {
    const $this = this;
    this.carousel = new Swiper(this.carouselElement, {
      watchSlidesProgress: true,
      init: $this.dataset.blocks > 7 || window.matchMedia("(max-width: 699px)").matches,
      slidesPerView: "auto",
      grabCursor: true,
      spaceBetween: 0,
      pagination: $this.dataset.pagination == "true" ? {
        el: $this.querySelector(".swiper-pagination"),
        clickable: true
      } : false
    });
  }
}


if (!customElements.get('reveal-items-custom')) {
  customElements.define('reveal-items-custom', RevealItemsCustom);
}
